<template>
  <v-card>
    <v-card-title> {{ model && model.Id ? "Edit Certificate" : "Add Certificate" }} </v-card-title>
    <v-card-text>
      <v-form ref="frmRegister" v-model="valid" lazy-validation>
        <v-row>
          <v-col cols="12">
            <InfoBox title="Add Your Certificates Here" />
          </v-col>
        </v-row>
        <v-row>
          <v-col md="6" cols="12">
            <TextField
              :readonly="isHiringmanager"
              showRequired
              dense
              :rules="[validations.required]"
              outlined
              label="Certificate Name"
              placeholder="Certificate Name"
              v-model="model.Name"
            />
          </v-col>
        </v-row>
        <v-row>
          <v-col md="6" cols="12">
            <AutoCompleteField
              :readonly="isHiringmanager"
              :rules="[validations.required, validations.arrayMinLength(1)]"
              showRequired
              label="Skill"
              persistent-hint
              hint="The first items of this field come from 'Technical Skills' that you can find it under Skills tab."
              dense
              placeholder="Select Skill..."
              v-model="model.SkillIds"
              :items="teammemberSkillList"
              item-title="Name"
              item-value="id"
              multiple
              chips
              deletable-chips
              outlined
            />
          </v-col>
        </v-row>
        <v-row>
          <v-col md="6" cols="12">
            <TextField
              :readonly="isHiringmanager"
              :rules="[validations.required]"
              showRequired
              dense
              outlined
              label="Issuing organisation"
              placeholder="Issuing Organisation"
              v-model="model.Organization"
            />
          </v-col>
        </v-row>
        <v-row>
          <v-col md="6" cols="12">
            <AutoCompleteField
              :readonly="isHiringmanager"
              :rules="[validations.required]"
              showRequired
              dense
              label="Year of Acquisition"
              placeholder="Year of Acquisition"
              v-model="model.Year"
              outlined
              :items="yearsList"
            />
          </v-col>
        </v-row>
        <v-row>
          <v-col md="6" cols="12">
            <FileUploadField
              v-model="currentFile"
              :show-size="showSize"
              :readonly="isHiringmanager"
              accept=".pdf,.doc,.docx,.png,.jpg"
              dense
              small-chips
              outlined
              label="Upload your Certificate File"
              prepend-icon=""
              @change="fileChanged"
            />
          </v-col>
        </v-row>
      </v-form>
    </v-card-text>
    <v-card-actions class="justify-end">
      <v-btn v-if="!isHiringmanager" class="secondary_btn" @click="cancel()" :loading="loading" prepend-icon="mdi-cancel"> Cancel</v-btn>
      <v-btn v-if="!isHiringmanager" class="primary_btn" @click="save()" :loading="loading" prepend-icon="mdi-check">{{
        model.Id == "" ? "Add Certificate" : "Save Certificate"
      }}</v-btn>
    </v-card-actions>
  </v-card>
</template>
<script lang="ts">
import { defineComponent } from "vue";
import { Certificate, TeammemberSkill } from "../../../models/Teammember";
import { Profile } from "../../../definitions/config";
import validations from "../../../utils/validations";
import CoreSkill from "../../../models/CoreSkill";
import AutoCompleteField from "../../Fields/AutoCompleteField.vue";
import TextField from "../../Fields/TextField.vue";
import FileUploadField from "../../Fields/FileUploadField.vue";
import InfoBox from "../../InfoBox.vue";

export default defineComponent({
  props: ["editingItem", "skills", "teammemberSkills", "addLoading", "isAfterSignUp", "isGettingCertificate", "isHiringmanager"],
  components: {
    AutoCompleteField,
    TextField,
    FileUploadField,
    InfoBox,
  },
  data() {
    return {
      currentFile: null as File | null,
      showSize: true,
      validations,
      teammemberSkillArray: [] as TeammemberSkill[],
      employmentTypes: Profile.Members.EmploymentTypes,
      locationTypes: Profile.Members.LocationTypes,
      endDatePickerMenu: false,
      startDatePickerMenu: false,
      loading: false,
      valid: true,
      startMenu: false,
      endMenu: false,
      certificateFile: null as File | null,
      model: {
        Id: "",
        Name: "",
        SkillIds: [] as string[],
        Organization: "",
        Year: null as number | null,
        CertificateFile: "",
      } as Certificate,
      technologyList: [] as string[],
    };
  },
  methods: {
    fileChanged() {
      var file = this.currentFile;
      this.showSize = true;
      this.certificateFile = file;
    },
    cancel(): void {
      this.$emit("cancel");
    },
    async save(): Promise<void> {
      const isValid = (await (this.$refs.frmRegister as any).validate()).valid;
      if (isValid) {
        this.$emit("saved", {
          model: this.model,
          callbackMethod: () => {
            this.resetForm();
            this.cancel();
          },
          certificateFile: this.certificateFile,
        });
      } else if (!this.isGettingCertificate) {
        var el = document.querySelector("#certificateTab .v-messages.error--text:first-of-type");
        this.$emit("scrollToElement", el);
      } else if (this.isGettingCertificate) {
        this.$emit("saved");
      }
    },
    resetForm() {
      this.model = {
        Id: "",
        Name: "",
        SkillIds: [] as string[],
        Organization: "",
        Year: null,
        CertificateFile: "",
        UploadCertificateUrl: null,
        DownloadCertificateUrl: null,
        TeammemberId: "",
      };
    },
    getSkillName(skillId: any) {
      var skillList = [...this.skills] as CoreSkill[];
      var skillItem = skillList.find((c) => c.id === skillId);
      if (skillItem) {
        return skillItem.Text;
      }
    },
  },
  async mounted() {
    this.currentFile = null;
    if (this.editingItem && this.editingItem.Id) {
      this.model = { ...this.editingItem };
      var filename = "";
      if (this.model.CertificateFile) {
        filename = this.model.CertificateFile.replace(this.model.Id, "");
        filename = this.model.CertificateFile.replace(this.editingItem.TeammemberId, "");
      }
      var splitedName = filename.split(".");
      if (splitedName.length > 1) {
        this.currentFile = new File([], filename);
      }
      this.showSize = false;
    } else {
      this.resetForm();
    }
  },
  computed: {
    yearsList() {
      var yearList = [] as number[];
      var currentYear = new Date().getFullYear();
      var lastYearCount = 30;
      for (let index = 0; index <= lastYearCount; index++) {
        yearList.push(currentYear - index);
      }
      return yearList;
    },
    teammemberSkillList() {
      var tmSkills = [...this.teammemberSkills] as TeammemberSkill[];
      var mappedSelectedSkills = tmSkills.map((c: any) => {
        var findSkill = this.skills.find((s: any) => s.id === c.SkillId);
        if (findSkill) {
          return { Name: findSkill.Text, id: c.SkillId };
        }
      });
      var tmSkillIds = tmSkills.map((c) => c.SkillId);
      var otherSkills = this.skills
        .filter((c: any) => !tmSkillIds.includes(c.id))
        .map((c: any) => {
          return { Name: c.Text, id: c.id };
        });
      mappedSelectedSkills.push(...otherSkills);
      return mappedSelectedSkills;
    },
  },
  watch: {
    isGettingCertificate(newVal) {
      if (newVal) {
        this.save();
      }
    },
    teammemberSkills(newVal) {
      this.teammemberSkillArray = newVal;
    },
    addLoading(newVal) {
      this.loading = newVal;
    },
  },
});
</script>
